import { createQuery } from "@nova/queryable"
import { AircraftComputedStatus, useAircraftStatuses } from "../aircraft/AircraftStatusQueries"
import { usePilotTrackingQuery } from "../PilotTrackingQueries"
import { useMemo, useState } from "react"
import { getDisplayableAircraftData } from "@nova/constants"
import * as Icons from "@nova/component-icons"
import { useRouter } from "pages"
import { setTab } from "../aircraft/slice"
import { useAppDispatch } from "integrations/redux/hooks"
import { AircraftColorTag } from "../aircraft/AircraftColorTag"

const aircraftsQuery = createQuery({
    resource: "aircraft",
    select: ["id", "nickname", "manufacturer", "model", "manual_status", "color"],
    aggregates: [
        { field: "missions.id", func: "COUNT", alias: "numFlights" },
        { field: "missions.metadata.airtime_hours", func: "SUM", alias: "totalFlightTime" },
        { field: "missions.metadata.started_at", func: "MAX", alias: "lastFlight" },
    ],
})

export function GroundedAircraftSection() {
    const { data: aircrafts } = usePilotTrackingQuery(aircraftsQuery)
    const statuses = useAircraftStatuses(aircrafts ?? [])
    const groundedAircrafts = useMemo(() => {
        if (!statuses || !aircrafts?.length) return []
        return Object.entries(statuses).reduce(
            (acc, [droneId, status]) => {
                if (
                    status === "grounded" ||
                    status === "maintenance_due" ||
                    status === "needs_maintenance"
                ) {
                    const drone = aircrafts?.find((a) => a.id === droneId)
                    if (drone) {
                        const { displayName, make, model } = getDisplayableAircraftData(drone)
                        return [
                            ...acc,
                            {
                                id: drone.id,
                                displayName,
                                make,
                                model,
                                color: drone.color,
                                status,
                            },
                        ]
                    }
                }
                return acc
            },
            [] as {
                id: string
                displayName: string
                make: string | null | undefined
                model: string | null | undefined
                color: string | null
                status: AircraftComputedStatus
            }[]
        )
    }, [aircrafts, statuses])
    if (!groundedAircrafts.length) return null
    if (groundedAircrafts.length === 1) {
        const a = groundedAircrafts[0]
        return (
            <GroundedAircraftWarning
                key={a.id}
                id={a.id}
                displayName={a.displayName}
                make={a.make}
                model={a.model}
                color={a.color}
            />
        )
    }
    return <MultipleGroundedAircraftWarning count={groundedAircrafts.length} />
}

function GroundedAircraftWarning({
    id,
    displayName,
    make,
    model,
    color,
}: {
    id: string
    displayName: string
    make: string | null | undefined
    model: string | null | undefined
    color: string | null
}) {
    const dispatch = useAppDispatch()
    const [hasDismissed, setHasDismissed] = useState(false)
    const router = useRouter("/workspace/:workspaceId")
    const droneMakeModel = useMemo(() => {
        if (make && model) return `${make} ${model}`
        if (make) return make
        if (model) return model
        return "drone"
    }, [make, model])
    if (hasDismissed) return null
    return (
        <div className="bg-[rgba(50,16,7,0.4)] border border-red-700 px-3 py-2 rounded-2xl flex items-center gap-8">
            <div className="flex gap-3 items-center flex-1">
                <div className="w-11 aspect-square rounded-lg bg-red-900 flex justify-center items-center relative">
                    <Icons.LargeIcon Icon={Icons.Exclamation} className="text-red-500" />
                </div>
                <div className="flex gap-3">
                    <AircraftColorTag color={color} className="my-2" />
                    <div className="flex flex-col">
                        <div className="text-white">{displayName}</div>
                        <small className="text-red-200">Grounded</small>
                    </div>
                </div>
            </div>
            <small className="shrink-0 text-gray-50">
                Your {droneMakeModel} needs maintenance.
            </small>
            <div className="flex items-center flex-1 justify-end gap-2">
                <div
                    className="flex items-center text-gray-100 hover:text-gray-50 cursor-pointer"
                    onClick={() => {
                        dispatch(setTab("maintenance"))
                        router.navigate({
                            path: "/fleet/aircraft/:aircraftId",
                            params: { aircraftId: id },
                        })
                    }}
                >
                    <div>Go to Maintenance</div>
                    <Icons.Icon Icon={Icons.ArrowRight} />
                </div>
                <div
                    onClick={() => setHasDismissed(true)}
                    className="text-gray-100 hover:text-gray-50 cursor-pointer"
                >
                    <Icons.Icon Icon={Icons.XClose} />
                </div>
            </div>
        </div>
    )
}

function MultipleGroundedAircraftWarning({ count }: { count: number }) {
    const [hasDismissed, setHasDismissed] = useState(false)
    const router = useRouter("/workspace/:workspaceId")
    if (hasDismissed) return null
    return (
        <div className="bg-[rgba(50,16,7,0.4)] border border-red-700 px-3 py-2 rounded-2xl flex items-center gap-8">
            <div className="flex gap-3 items-center flex-1">
                <div className="w-11 aspect-square rounded-lg bg-red-900 flex justify-center items-center relative">
                    <Icons.LargeIcon Icon={Icons.Exclamation} className="text-red-500" />
                </div>
                <div className="flex flex-col">
                    <div className="text-white">{count} Drones</div>
                    <small className="text-red-200">Grounded</small>
                </div>
            </div>
            <div className="flex items-center flex-1 justify-end gap-2">
                <router.Link to={{ path: "/fleet/aircraft" }}>
                    <div className="flex items-center text-gray-100 hover:text-gray-50 cursor-pointer">
                        <div>Go to Maintenance</div>
                        <Icons.Icon Icon={Icons.ArrowRight} />
                    </div>
                </router.Link>
                <div
                    onClick={() => setHasDismissed(true)}
                    className="text-gray-100 hover:text-gray-50 cursor-pointer"
                >
                    <Icons.Icon Icon={Icons.XClose} />
                </div>
            </div>
        </div>
    )
}
